<script setup>
import { onMounted, ref, watch, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useFunctions from '@/composables/useFunctions';
import SajuIljin from '@/pages/mobile/saju-iljin/index.vue';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import { useStore } from 'vuex';
import { useAnalysisStore } from '@/store/modules/analysis';

const useFun = useFunctions();
const router = useRouter();
const route = useRoute();
const store = useStore();
const analysisStore = useAnalysisStore();
const props = defineProps({
  prInputdata: {
    type: Object,
    default: () => ({}),
  },
  prSajuData: {
    type: Object,
    default: () => ({}),
  },
  prBirthData: {
    type: Object,
    default: () => ({}),
  },
  langMode: {
    type: String,
    default: 'KOR',
  },
  five: {
    type: Object,
    default: () => ({}),
  },
  transdata: {
    type: Object,
    default: () => ({}),
  },
  // AGENT: 백엔드에서 제공하는 정확한 양력/음력 날짜
  solar: {
    type: String,
    default: null,
  },
  lunar: {
    type: String,
    default: null,
  },
  pgmode: {
    type: String,
    default: '',
  },
  isIlganClosed: {
    type: Boolean,
    default: false,
  },
  rsGuestUserId: {
    type: Number,
    default: null,
  },
  isUnknownTime: {
    type: Boolean,
    default: false,
  },
  isFutureSaju: {
    type: Boolean,
    default: false,
  },
  hideBar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['closePopQuickMenu', 'regSave', 'clickIlganButton', 'redirectToLogin']);

const popQuickMenu = ref(true);
const showSajuIljinDialog = ref(false);
const activeLink = ref(null);

// 스와이프 관련 변수
const swiperContainer = ref(null);
const scrollPosition = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

// 스와이프 관련 함수
const checkScrollButtons = () => {
  if (!swiperContainer.value) return;
  const container = swiperContainer.value;
  canScrollLeft.value = container.scrollLeft > 0;
  canScrollRight.value = container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
};

const scrollLeftFunc = () => {
  if (!swiperContainer.value) return;
  const container = swiperContainer.value;
  const scrollAmount = 200;
  container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  setTimeout(checkScrollButtons, 300);
};

const scrollRightFunc = () => {
  if (!swiperContainer.value) return;
  const container = swiperContainer.value;
  const scrollAmount = 200;
  container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  setTimeout(checkScrollButtons, 300);
};

// 터치 이벤트 핸들러
const handleTouchStart = (e) => {
  isDragging.value = true;
  startX.value = e.touches[0].pageX - swiperContainer.value.offsetLeft;
  scrollLeft.value = swiperContainer.value.scrollLeft;
};

const handleTouchMove = (e) => {
  if (!isDragging.value) return;
  e.preventDefault();
  const x = e.touches[0].pageX - swiperContainer.value.offsetLeft;
  const walk = (x - startX.value) * 2;
  swiperContainer.value.scrollLeft = scrollLeft.value - walk;
};

const handleTouchEnd = () => {
  isDragging.value = false;
  checkScrollButtons();
};

// 마우스 이벤트 핸들러 (데스크톱에서도 드래그 가능하도록)
const handleMouseDown = (e) => {
  isDragging.value = true;
  startX.value = e.pageX - swiperContainer.value.offsetLeft;
  scrollLeft.value = swiperContainer.value.scrollLeft;
  swiperContainer.value.style.cursor = 'grabbing';
};

const handleMouseMove = (e) => {
  if (!isDragging.value) return;
  e.preventDefault();
  const x = e.pageX - swiperContainer.value.offsetLeft;
  const walk = (x - startX.value) * 2;
  swiperContainer.value.scrollLeft = scrollLeft.value - walk;
};

const handleMouseUp = () => {
  isDragging.value = false;
  if (swiperContainer.value) {
    swiperContainer.value.style.cursor = 'grab';
  }
  checkScrollButtons();
};

const handleMouseLeave = () => {
  isDragging.value = false;
  if (swiperContainer.value) {
    swiperContainer.value.style.cursor = 'grab';
  }
};

onMounted(() => {
  // DOM이 완전히 렌더링된 후 초기화
  setTimeout(() => {
    if (swiperContainer.value) {
      swiperContainer.value.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
    }
  }, 100);

  // 리사이즈 이벤트 추가
  window.addEventListener('resize', checkScrollButtons);
});

onUnmounted(() => {
  if (swiperContainer.value) {
    swiperContainer.value.removeEventListener('scroll', checkScrollButtons);
  }
  window.removeEventListener('resize', checkScrollButtons);
});

const close = () => emit('closePopQuickMenu');

const redirectToLogin = ({ redirectOption }) => {
  emit('redirectToLogin', { redirectOption });
};

const handleRegSave = (callback) => {
  emit('regSave', callback);
};
const showHanja = (value, position) => {
  return props.langMode !== 'KOR' ? props.five.getHanjaTxt(value, position) : value;
};
const clickIlganButton = () => {
  activeLink.value = 'ilgan';
  emit('clickIlganButton', '일반');
};

const dialogTitle = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');
const dialogConfirm = ref(null);
const loginRedirectOption = ref(null);
const dialogAlert = ref(null);

const analysisMenus = {
  reading: '/overviewSajuSummary',
  disposition: '/overviewTendency',
  analysis: '/overviewYongsin',
};

const navigateToAnalysis = (type) => {
  const menu = analysisMenus[type];
  if (!menu) return;
  if (!store.getters['user/id'] && !sessionStorage.getItem('X-Token')) {
    loginRedirectOption.value = {
      reading: 'reading',
      disposition: 'personality',
      analysis: 'yongsin',
    }[type];
    dialogConfirm.value.openConfirmDialog();
    return;
  }
  analysisStore.selectedMenu = menu;
  const sajuId = route.params.id || props.rsGuestUserId;
  if (Number.isInteger(Number(sajuId)) && Number(sajuId) > 0) {
    if (props.hideBar) {
      const mobilePage = { reading: 'basic', disposition: 'personality', analysis: 'yongsin' }[type];
      router.push({ path: `/m/saju-insight/${mobilePage}`, query: { id: sajuId } });
    } else {
      router.push(`/analysis/${sajuId}`);
    }
  } else {
    // 저장 성공 안내의 확인 동작이 사주 목록으로 이동한다.
    handleRegSave();
  }
};

const showSajuIljin = () => {
  activeLink.value = 'iljin';
  showSajuIljinDialog.value = true;
};

const closeSajuIljinDialog = () => {
  activeLink.value = null;
  showSajuIljinDialog.value = false;
};

watch(
  () => props.isIlganClosed,
  (newVal) => {
    if (newVal === true) activeLink.value = null;
  },
);

console.log(props.isIlganClosed);

// =============== 낭만일진 ===============
/**
 * 한글변환
 * text: string, type: 'sky' | 'earth'
 */
const getKorText = (text, type) => {
  const chn = type === 'sky' ? skyChn : earthChn;
  const kor = type === 'sky' ? skyKor : earthKor;

  // 입력된 한자의 index 찾기
  const idx = chn.indexOf(text);
  if (idx !== -1) return kor[idx];
  return text;
};

/** 낭만일진 요약 팝업 params 생성 */
const summaryParams = computed(() => {
  if (!props.prSajuData) return;

  const { birthganji, transdata, inputdata } = props.prSajuData ?? {};
  if (!birthganji || !transdata || !inputdata) return;

  const { sky, earth } = birthganji;

  const baseCode = [
    getKorText(sky.hour?.value, 'sky'),
    getKorText(earth.hour?.value, 'earth'),
    getKorText(sky.day?.value, 'sky'),
    getKorText(earth.day?.value, 'earth'),
    getKorText(sky.month?.value, 'sky'),
    getKorText(earth.month?.value, 'earth'),
    getKorText(sky.year?.value, 'sky'),
    getKorText(earth.year?.value, 'earth'),
  ].join('');

  const convertedParams = {
    baseCode,
    // props.solar가 있으면 사용
    birth: props.solar,
    lang: 'KOR',
    gender: inputdata.gender === '0' ? 0 : 1,
  };

  return convertedParams;
});

/** 상세분석 데이터 접근 불가 사주 검사 */
// 시간모름, 미래사주
const validateAnalysisAccess = (type) => {
  if (props.isFutureSaju) {
    showFutureSajuConfirm();
    return;
  }
  if (props.isUnknownTime) {
    showUnknownTimeConfirm();
    return;
  }
  switch (type) {
    case 'reading':
      navigateToAnalysis('reading');
      break;
    case 'iljin':
      showSajuIljin();
      break;
    case 'disposition':
      navigateToAnalysis('disposition');
      break;
    case 'analysis':
      navigateToAnalysis('analysis');
      break;
  }
};
defineExpose({
  navigateToAnalysis: validateAnalysisAccess,
  openSajuIljinDialog: () => validateAnalysisAccess('iljin'),
});

const showUnknownTimeConfirm = () => {
  showAnalysisAlert({
    title: `시간 모름인 사주는\n상세분석 서비스 제공이 어렵습니다.`,
  });
};

const showFutureSajuConfirm = () => {
  showAnalysisAlert({
    title: `내년 이후 사주는\n상세분석 서비스 제공이 어렵습니다.`,
  });
};
const showAnalysisAlert = ({ title }) => {
  dialogTitle.value = title;
  dialogText.value = '';
  dialogAlert.value.openAlertDialog();
};
</script>

<template>
  <div class="pop-saju-quick-menu" :class="{ 'hide-bar': hideBar }">
    <div v-if="!hideBar" class="quick-inner-box">
      <div class="user-info">
        <div class="user-txt">
          <span>
            {{
              showHanja(prBirthData?.sky?.day?.value, 'sky') || prBirthData?.sky?.day?.value
            }}</span
          >
        </div>
        <div class="user-name-box">
          <div class="name">{{ prInputdata?.name || '-' }}</div>
          <div class="level">
            {{ useFun.getGenderTxt(prInputdata?.gender) }} {{ transdata?.kor_age }}세 (만{{
              transdata?.man_age
            }}세)
          </div>
        </div>
      </div>
      <div class="quick-links-wrapper">
        <button
          class="swipe-nav-btn swipe-nav-btn-left"
          :class="{ disabled: !canScrollLeft }"
          @click="scrollLeftFunc"
          aria-label="이전"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          class="quick-links"
          ref="swiperContainer"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
        >
          <!-- [HIDDEN] 일간특성, 낭만일진 - 추후 필요 시 주석 해제 -->
          <!-- <div class="link-item" @click="clickIlganButton">
            <img
              v-if="activeLink === 'ilgan'"
              src="@/assets/images_manse/main/img_main-now-viewing.png"
              class="overlay-image"
            />
            <div
              class="img-box"
              @click="clickIlganButton"
              :class="[
                five.getColorFive(
                  showHanja(prBirthData?.sky?.day?.value, 'sky') || prBirthData?.sky?.day?.value,
                ),
              ]"
            >
              {{ showHanja(prBirthData?.sky?.day?.value, 'sky') || prBirthData?.sky?.day?.value }}
            </div>
            <span>일간특성</span>
          </div> -->
          <div class="link-item" @click="validateAnalysisAccess('reading')">
            <span class="badge-new">NEW</span>
            <img src="@/assets/images_manse/main/img_main-reading-sm.png" alt="사주풀이" />
            <span>사주풀이</span>
          </div>
          <!-- <div class="link-item" @click="validateAnalysisAccess('iljin')">
            <img
              v-if="activeLink === 'iljin'"
              src="@/assets/images_manse/main/img_main-now-viewing.png"
              class="overlay-image"
            />
            <img src="@/assets/images_manse/main/img_main-saju-sm.png" alt="낭만일진" />
            <span>낭만일진</span>
          </div> -->
          <div class="link-item" @click="validateAnalysisAccess('disposition')">
            <img src="@/assets/images_manse/main/img_main-personality-sm.png" alt="성향분석" />
            <span>성향분석</span>
          </div>
          <div class="link-item" @click="validateAnalysisAccess('analysis')">
            <img src="@/assets/images_manse/main/img_main-payment-sm-new.png" alt="용신분석" />
            <span>용신분석</span>
          </div>
        </div>
        <button
          class="swipe-nav-btn swipe-nav-btn-right"
          :class="{ disabled: !canScrollRight }"
          @click="scrollRightFunc"
          aria-label="다음"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <div class="quick-btn-box">
        <button class="button" v-if="pgmode !== 'rs'" @click="handleRegSave()">저장하기</button>
        <button class="button list" v-if="pgmode == 'rs'" @click="router.push('/saju-service')">
          <i class="return-icon"></i>목록으로
        </button>
      </div>
    </div>

    <!-- 낭만일진 다이얼로그 -->
    <v-dialog v-model="showSajuIljinDialog" fullscreen>
      <div class="saju-iljin-dialog saju-pop-common-dialog">
        <div class="dialog-content">
          <SajuIljin
            :summaryParams="summaryParams"
            @close="closeSajuIljinDialog"
            @redirectToLogin="redirectToLogin"
          />
        </div>
      </div>
    </v-dialog>
  </div>

  <Teleport to="body">
    <ConfirmComponent
      ref="dialogConfirm"
      title="로그인 후 이용해 주세요."
      text="로그인"
      :nextUrl="() => redirectToLogin({ redirectOption: loginRedirectOption })"
    />
  </Teleport>
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
<style lang="scss">
@keyframes bounceUp {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  50% {
    transform: translateY(-1px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.pop-saju-quick-menu {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 104px;
  flex-shrink: 0;
  z-index: 100;
  animation: bounceUp 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;

  &.hide-bar {
    position: static;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
    animation: none;
  }

  .quick-inner-box {
    width: 100%;
    max-width: 1360px;
    height: 100%;
    margin: 0 auto;
    align-items: center;
    justify-content: space-between;
    padding: 0 36px;
    display: flex;
    border-radius: 20px 20px 0px 0px;
    border-top: 1px solid var(--Gray-300, #ebebeb);
    border-right: 1px solid var(--Gray-300, #ebebeb);
    border-left: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    box-shadow: 0px -10px 40px 0px rgba(0, 0, 0, 0.2);

    > * {
      flex: 1;
      width: 33.333%;
    }
  }

  .user-info {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    .user-txt {
      display: flex;
      width: 44px;
      height: 44px;
      justify-content: center;
      align-items: center;
      border-radius: 50px;
      background: var(--Gray-300, #ebebeb);
      color: var(--Gray-800, #333);
      text-align: center;
      font-family: 'ChosunGs';
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 100% */
    }
    .user-name-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      .name {
        color: var(--Gray-800, #333);
        font-family: Pretendard;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 22px; /* 122.222% */
        letter-spacing: -0.45px;
      }
      .level {
        color: var(--Gray-700, #575757);
        font-family: Pretendard;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.35px;
      }
    }
  }
  .quick-links-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    flex: 1;
    width: 33.333%;

    .swipe-nav-btn {
      display: none;
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      color: var(--Gray-800, #333);
      cursor: pointer;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      z-index: 1;

      &:hover:not(.disabled) {
        background: var(--Gray-100, #f5f5f5);
        border-color: var(--Gray-400, #d0d0d0);
      }

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .quick-links {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 40px;
      flex: 1;
      overflow: hidden;

      .link-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        position: relative;
        flex-shrink: 0;
        &:hover {
          span {
            color: var(--orange-300-main, #eb4c10);
            font-weight: 700;
          }
        }
        .overlay-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 48px;
          height: 48px;
        }
        .badge-new {
          position: absolute;
          top: 0px;
          right: -8px;
          display: flex;
          height: 18px;
          padding: 3px 6px;
          justify-content: center;
          align-items: center;
          border-radius: 4px;
          background: #ffb401 !important;
          color: #fff !important;
          font-family: Pretendard !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          line-height: 12px !important;
          z-index: 1;
        }
        .img-box {
          display: flex;
          width: 48px;
          height: 48px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          border-radius: 24px;
          border: 1px solid rgba(118, 118, 118, 0.5);
          background: linear-gradient(180deg, #c9c9c9 0%, #979797 100%);
          color: var(--gray-100-white, #fff);
          text-align: center;
          text-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
          font-family: 'ChosunGs';
          font-size: 24px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px; /* 100% */
          &.five01 {
            border: 1px solid #c23535;
            background: linear-gradient(180deg, #ff9696 0%, #c23535 100%);
          }
          &.five02 {
            border: 1px solid rgba(64, 64, 64, 0.5);
            background: linear-gradient(180deg, #969696 0%, #404040 100%);
          }
          &.five03 {
            border: 1px solid #5e9f1c;
            background: linear-gradient(180deg, #abd97c 0%, #60a21d 100%);
          }
          &.five04 {
          }
          &.five05 {
            border: 1px solid rgba(216, 153, 43, 0.5);
            background: linear-gradient(180deg, #fdcb75 0%, #db9823 100%);
          }
        }
        img {
          width: 48px;
          height: 48px;
        }
        span {
          color: var(--Gray-800, #333);
          text-align: center;
          font-family: Pretendard;
          font-size: 13px;
          font-style: normal;
          font-weight: 500;
          line-height: 18px; /* 138.462% */
        }
      }
    }
  }

  // 반응형 스타일 (모바일에서만 스와이프 활성화)
  @media (max-width: 1024px) {
    .quick-inner-box {
      > * {
        flex: 0 0 auto;
        width: auto;
      }

      .user-info {
        flex: 0 0 auto;
        min-width: 0;
      }

      .quick-links-wrapper {
        flex: 1;
        min-width: 0;
        overflow: hidden;

        .swipe-nav-btn {
          display: flex;
        }

        .quick-links {
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
          cursor: grab;
          gap: 24px;
          padding: 0 8px;

          &::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }

          &::-webkit-scrollbar-thumb {
            display: none;
          }

          .link-item {
            min-width: fit-content;
            flex-shrink: 0;
          }
        }
      }

      .quick-btn-box {
        flex: 0 0 auto;
        min-width: fit-content;
      }
    }
  }

  .quick-btn-box {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    .button {
      display: flex;
      width: 100px;
      padding: 10px 16px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
      &.list {
        width: fit-content;
        border: 1px solid var(--Gray-300, #ebebeb);
        background: var(--gray-100-white, #fff);
        color: var(--Gray-800, #333);
        text-align: center;
        font-family: Pretendard;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 18px;
        .return-icon {
          background-image: url('@manseImages/icon_return.svg');
          background-size: contain;
          width: 16px;
          aspect-ratio: 1/1;
        }
      }
    }
  }
}

// 다이얼로그 스타일
.saju-pop-common-dialog {
  margin: 52px auto;
  width: 740px;
  height: 694px;
  background: #fff;
  position: relative;
  border-radius: 16px;

  .dialog-content {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 16px;
  }
}

@media (max-width: 1024px) {
  .saju-pop-common-dialog {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
    height: 100vh !important;
    height: 100dvh !important;
  }
}
</style>
